import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Workflow,
  Layers,
  Lock,
  Check,
  ArrowRight,
  ShieldCheck,
  HeartPulse,
  Server,
  EyeOff,
  Activity,
} from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

const INTEGRATIONS = [
  "Litify",
  "Filevine",
  "SmartAdvocate",
  "Salesforce",
  "RingCentral",
  "Five9",
  "Dialpad",
  "Zoom",
];

const ACTIONS = [
  { label: "Answer FAQ · office hours & directions", c: 99 },
  { label: "Route a new-client call", c: 97 },
  { label: "Log intake to the CMS", c: 95 },
  { label: "Qualify a lead", c: 88 },
  { label: "Schedule a consultation", c: 82 },
  { label: "Open a claim", c: 71 },
  { label: "De-escalate an upset caller", c: 64 },
  { label: "Give legal guidance", locked: true },
];

const TRUST = [
  { Icon: ShieldCheck, text: "SOC 2 (Type II underway)" },
  { Icon: HeartPulse, text: "HIPAA-aligned" },
  { Icon: Lock, text: "End-to-end encrypted" },
  { Icon: Server, text: "US-hosted" },
  { Icon: EyeOff, text: "Never trains shared models" },
  { Icon: Activity, text: "99% uptime · 24/7 monitoring" },
];

export const FitSlide = ({ slide }) => {
  const reduce = useReducedMotion();
  const [line, setLine] = useState(85);

  const classified = ACTIONS.map((a) => ({
    ...a,
    handles: !a.locked && a.c >= line,
  }));
  const handleCount = classified.filter((a) => a.handles).length;
  const reviewCount = classified.length - handleCount;
  const firstReviewIndex = classified.findIndex((a) => !a.handles);
  const linePct = ((line - 50) / (98 - 50)) * 100;

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.11, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 8}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(18px,3.6%,40px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>How it fits</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            09 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Your workflows. Your stack. Your control.
        </motion.h1>
        <motion.p variants={item} className="font-sans text-[14px] text-ink-soft mt-2">
          Lexafide fits how your firm already runs — and never takes the wheel
          without your say.
        </motion.p>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-[0.82fr_1.18fr] gap-5 mt-5">
          {/* LEFT cards */}
          <div className="flex flex-col gap-4 min-h-0">
            <motion.div
              variants={item}
              className="rounded-2xl border border-hairline bg-[#FBFAF6] p-5"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-accent/12 flex items-center justify-center">
                  <Workflow className="w-4 h-4 text-accent" />
                </span>
                <h3 className="font-display font-medium text-lg text-ink">
                  Your workflows
                </h3>
              </div>
              <p className="font-sans text-[13px] text-ink-soft leading-relaxed mt-3">
                Agents are built around your intake, your questions, and your rules
                — not a generic template dropped on top of your firm.
              </p>
            </motion.div>

            <motion.div
              variants={item}
              className="rounded-2xl border border-hairline bg-[#FBFAF6] p-5 flex-1 min-h-0"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-accent/12 flex items-center justify-center">
                  <Layers className="w-4 h-4 text-accent" />
                </span>
                <h3 className="font-display font-medium text-lg text-ink">Your stack</h3>
              </div>
              <p className="font-sans text-[13px] text-ink-soft leading-relaxed mt-3">
                Native to the tools you already run. Nothing gets re-keyed — and
                your data never leaves where it lives.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-3.5">
                {INTEGRATIONS.map((n) => (
                  <span
                    key={n}
                    className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-soft border border-hairline rounded-full px-2.5 py-1"
                  >
                    {n}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4 pt-3.5 border-t border-hairline">
                <Lock className="w-3.5 h-3.5 text-accent shrink-0" />
                <span className="font-sans text-[12px] text-ink-soft">
                  Your data stays in your systems — it never trains shared models.
                </span>
              </div>
            </motion.div>
          </div>

          {/* RIGHT control panel */}
          <motion.div
            variants={item}
            data-testid="control-panel"
            className="rounded-2xl p-5 flex flex-col min-h-0 text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-medium text-xl text-paper">
                  Your control
                </h3>
                <p className="font-sans text-[12px] text-paper/60 mt-0.5 max-w-xs">
                  You set the confidence line. Below it, a human decides.
                </p>
              </div>
              <span
                data-testid="confidence-line"
                className="font-mono text-2xl text-[#93B4FF] tabular-nums"
              >
                {line}%
              </span>
            </div>

            {/* slider */}
            <input
              data-testid="confidence-slider"
              type="range"
              min={50}
              max={98}
              step={1}
              value={line}
              onChange={(e) => setLine(Number(e.target.value))}
              aria-label="Confidence threshold"
              className="range-lex mt-4"
              style={{
                background: `linear-gradient(to right, #1E5BFF 0%, #1E5BFF ${linePct}%, rgba(255,255,255,0.14) ${linePct}%, rgba(255,255,255,0.14) 100%)`,
              }}
            />

            {/* counts */}
            <div className="flex items-center gap-6 mt-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-[#93B4FF]">
                Agent handles {handleCount}
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-paper/55">
                Routes to you {reviewCount}
              </span>
            </div>

            {/* action rows */}
            <div className="mt-3 flex-1 min-h-0 overflow-y-auto pr-1 space-y-1">
              {classified.map((a, i) => (
                <React.Fragment key={i}>
                  {i === firstReviewIndex ? (
                    <div
                      data-testid="your-line-marker"
                      className="flex items-center gap-2 pt-1.5"
                    >
                      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-accent border border-accent/50 rounded-full px-1.5 py-0.5">
                        Your line
                      </span>
                      <span className="flex-1 border-t border-dashed border-accent/60" />
                    </div>
                  ) : null}
                  <div
                    data-testid={`action-row-${i + 1}`}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                      a.handles
                        ? "bg-accent/[0.12] border border-accent/25"
                        : "border border-transparent"
                    }`}
                  >
                    <span className="flex-1 font-sans text-[13px] text-paper/90 leading-tight">
                      {a.label}
                    </span>
                    {a.locked ? (
                      <Lock className="w-3.5 h-3.5 text-paper/40" />
                    ) : (
                      <span className="font-mono text-[11px] text-paper/55 tabular-nums w-8 text-right">
                        {a.c}
                      </span>
                    )}
                    {a.handles ? (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[#93B4FF] w-[104px] justify-end">
                        <Check className="w-3 h-3" strokeWidth={3} />
                        Agent handles
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.08em] text-paper/55 w-[104px] justify-end">
                        <ArrowRight className="w-3 h-3" />
                        You review
                      </span>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>

            <div className="pt-3 mt-1 border-t border-white/10">
              <p className="font-sans text-[12px] text-paper/70">
                Pause, override, or{" "}
                <span className="text-[#93B4FF] font-medium">roll back any action</span>{" "}
                in one click.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Trust bar */}
        <motion.div
          variants={item}
          className="border-t border-hairline pt-4 mt-4 flex flex-wrap gap-2"
        >
          {TRUST.map(({ Icon, text }) => (
            <span
              key={text}
              className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-3 py-1.5"
            >
              <Icon className="w-3.5 h-3.5 text-accent" />
              <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-soft">
                {text}
              </span>
            </span>
          ))}
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default FitSlide;
